/**
 * Copyright © 2026 Delicious AI, LLC
 */

import {
  Box,
  Dialog,
  DialogContent,
  DialogTitle,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material'

import { PlanogramChangeDetailsFragment } from '@/graphql'
import useT from '@/i18n/useT'

import { summaryText, userName } from './planogram-history.helpers'
import PlanogramHistoryVisual from './PlanogramHistoryVisual'

type Props = {
  change: PlanogramChangeDetailsFragment | null
  onClose: () => void
}

export default function PlanogramHistoryDialog({ change, onClose }: Props) {
  const { t } = useT()
  return (
    <Dialog open={change !== null} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        {t('planogramHistory.dialog.title', 'Planogram changes')}
      </DialogTitle>
      <DialogContent sx={{ overflowX: 'hidden' }}>
        {change && (
          <>
            <Box sx={{ mb: 2 }}>
              <Typography>{summaryText(change, t)}</Typography>
              <Typography variant="caption" color="text.secondary">
                {t(
                  'planogramHistory.dialog.highlightHelp',
                  'Green facings changed. Hover over one to compare the previous and new products.',
                )}
              </Typography>
            </Box>
            <PlanogramHistoryVisual change={change} />
            <TableContainer sx={{ width: '100%', overflowX: 'auto' }}>
              <Table size="small" sx={{ minWidth: 600 }}>
                <TableHead>
                  <TableRow>
                    <TableCell>
                      {t('planogramHistory.dialog.columns.upc', 'UPC')}
                    </TableCell>
                    <TableCell>
                      {t('planogramHistory.dialog.columns.change', 'Change')}
                    </TableCell>
                    <TableCell align="right">
                      {t(
                        'planogramHistory.dialog.columns.previousFacings',
                        'Previous facings',
                      )}
                    </TableCell>
                    <TableCell align="right">
                      {t(
                        'planogramHistory.dialog.columns.newFacings',
                        'New facings',
                      )}
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {change.diff.products.map(product => (
                    <TableRow key={product.upc}>
                      <TableCell>{product.upc}</TableCell>
                      <TableCell>
                        {t(
                          `planogramHistory.changeType.${product.changeType.toLowerCase()}`,
                          product.changeType.toLowerCase().replaceAll('_', ' '),
                        )}
                      </TableCell>
                      <TableCell align="right">
                        {product.previousFacingCount}
                      </TableCell>
                      <TableCell align="right">
                        {product.newFacingCount}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            {change.completedAt && (
              <Typography color="text.secondary" sx={{ mt: 2 }}>
                {t(
                  'planogramHistory.dialog.completedBy',
                  'Completed {completedAt} by {completedBy}',
                  {
                    completedAt: new Date(change.completedAt).toLocaleString(),
                    completedBy: userName(
                      change.completedBy,
                      t('common.unknown', 'Unknown'),
                    ),
                  },
                )}
              </Typography>
            )}
          </>
        )}
      </DialogContent>
    </Dialog>
  )
}
